/**
 * Performance monitoring utilities
 */

/**
 * Measures and logs page load performance
 */
export const measurePageLoad = (): void => {
  if (typeof window !== "undefined" && "performance" in window) {
    window.addEventListener("load", () => {
      setTimeout(() => {
        const perfData = performance.getEntriesByType(
          "navigation"
        )[0] as PerformanceNavigationTiming;

        if (perfData) {
          const metrics = {
            dns: Math.round(
              perfData.domainLookupEnd - perfData.domainLookupStart
            ),
            connection: Math.round(perfData.connectEnd - perfData.connectStart),
            response: Math.round(perfData.responseEnd - perfData.responseStart),
            dom: Math.round(
              perfData.domContentLoadedEventEnd -
                perfData.domContentLoadedEventStart
            ),
            load: Math.round(perfData.loadEventEnd - perfData.loadEventStart),
            total: Math.round(perfData.loadEventEnd - perfData.fetchStart),
          };

          console.log("Performance Metrics:", metrics);

          // Log warnings for slow performance
          if (metrics.total > 3000) {
            console.warn("Slow page load detected:", metrics.total + "ms");
          }
        }
      }, 0);
    });
  }
};

/**
 * Measures component render time
 */
export const measureRenderTime = (componentName: string) => {
  const startTime = performance.now();

  return () => {
    const endTime = performance.now();
    const renderTime = Math.round(endTime - startTime);

    if (renderTime > 100) {
      console.warn(
        `Slow render detected for ${componentName}: ${renderTime}ms`
      );
    } else {
      console.log(`${componentName} rendered in ${renderTime}ms`);
    }
  };
};

/**
 * Lazy load images with intersection observer
 */
export const createImageObserver = (
  callback: (entry: IntersectionObserverEntry) => void
) => {
  if ("IntersectionObserver" in window) {
    return new IntersectionObserver(
      (entries) => {
        entries.forEach(callback);
      },
      {
        rootMargin: "50px 0px",
        threshold: 0.01,
      }
    );
  }
  return null;
};

/**
 * Preload critical resources
 */
export const preloadCriticalResources = (resources: string[]): void => {
  resources.forEach((resource) => {
    const link = document.createElement("link");
    link.rel = "preload";
    link.href = resource;

    // Determine resource type
    if (resource.match(/\.(woff2?|ttf|otf)$/)) {
      link.as = "font";
      link.crossOrigin = "anonymous";
    } else if (resource.match(/\.(jpg|jpeg|png|webp|svg)$/)) {
      link.as = "image";
    } else if (resource.match(/\.css$/)) {
      link.as = "style";
    }

    document.head.appendChild(link);
  });
};
